import { BlogPost } from "@/types/blog";
import { processImage } from "@/utils/imageProcessing";
import { processContentImages } from "@/utils/wordpressMarkdownGenerator";

/**
 * Read file as text
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target?.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
};

/**
 * Process WordPress JSON posts into BlogPost format with image processing
 */
export const processWordPressJsonPosts = async (posts: any[]): Promise<BlogPost[]> => {
  if (!Array.isArray(posts)) {
    console.error("Expected posts to be an array, received:", typeof posts);
    return [];
  }
  
  const processedPosts = [];
  
  for (const post of posts.filter(post => post && post.title)) {
    try {
      const processedPost = await processWordPressJsonPost(post);
      if (processedPost) {
        processedPosts.push(processedPost);
      }
    } catch (error) {
      console.error("Error processing post:", error);
      // Continue with next post
    }
  }
  
  return processedPosts;
};

/**
 * Process a single WordPress JSON post into BlogPost format
 */
export const processWordPressJsonPost = async (post: any): Promise<BlogPost | null> => {
  if (!post || !post.title) return null;
  
  try {
    // Extract title - handle different formats
    const title = typeof post.title === 'string' 
      ? post.title 
      : post.title?.rendered || post.title?.raw || "Untitled";
    
    // Extract content - handle different formats
    const content = typeof post.content === 'string'
      ? post.content
      : post.content?.rendered || post.content?.raw || "";
    
    // Extract excerpt - handle different formats
    const excerpt = typeof post.excerpt === 'string'
      ? post.excerpt
      : post.excerpt?.rendered || post.excerpt?.raw || "";
    
    // Generate slug if not present
    const slug = post.slug || title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Convert date format
    const date = post.date 
      ? new Date(post.date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
    
    // Initialize tags array
    let tags: string[] = [];
    console.log(`Starting tag extraction for post "${title}"`);
    
    // ENHANCED TAG EXTRACTION LOGIC
    
    // 1. Try to extract tags from the 'tags' property
    if (post.tags) {
      console.log(`Post "${title}" has tags field:`, post.tags);
      
      if (Array.isArray(post.tags)) {
        // If tags is an array of strings
        post.tags.forEach((tag: any) => {
          if (typeof tag === 'string' && tag.trim()) {
            console.log(`Adding string tag: ${tag}`);
            tags.push(tag.trim());
          } 
          // If tags is an array of objects with name properties
          else if (tag && typeof tag === 'object' && tag.name) {
            if (typeof tag.name === 'string' && tag.name.trim()) {
              console.log(`Adding tag from object with name: ${tag.name}`);
              tags.push(tag.name.trim());
            }
          }
          // If tags is an array of objects with slug properties and no name
          else if (tag && typeof tag === 'object' && tag.slug && !tag.name) {
            if (typeof tag.slug === 'string' && tag.slug.trim()) {
              // Convert slug to title case for readability
              const readableTag = tag.slug
                .replace(/-/g, ' ')
                .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
              
              console.log(`Adding tag from object with slug: ${tag.slug} -> ${readableTag}`);
              tags.push(readableTag);
            }
          }
          // If tag is a number (ID reference)
          else if (typeof tag === 'number') {
            console.log(`Skipping numeric tag ID: ${tag}`);
          }
        });
      } 
      // If tags is an object with key-value pairs
      else if (typeof post.tags === 'object' && post.tags !== null) {
        Object.entries(post.tags).forEach(([key, value]) => {
          // If the value is a string
          if (typeof value === 'string' && value.trim()) {
            console.log(`Adding tag from object key-value: ${value}`);
            tags.push(value.trim());
          }
          // If the value is an object with a name property
          else if (value && typeof value === 'object' && (value as any).name) {
            if (typeof (value as any).name === 'string' && (value as any).name.trim()) {
              console.log(`Adding tag from nested object with name: ${(value as any).name}`);
              tags.push((value as any).name.trim());
            }
          }
        });
      }
    }
    
    // 2. Try to extract categories and add them as tags if they exist
    if (post.categories) {
      console.log(`Post "${title}" has categories field:`, post.categories);
      
      if (Array.isArray(post.categories)) {
        post.categories.forEach((category: any) => {
          if (typeof category === 'string' && category.trim()) {
            console.log(`Adding string category as tag: ${category}`);
            tags.push(category.trim());
          } 
          else if (category && typeof category === 'object' && category.name) {
            if (typeof category.name === 'string' && category.name.trim()) {
              console.log(`Adding category from object with name: ${category.name}`);
              tags.push(category.name.trim());
            }
          }
          // If category is a slug
          else if (category && typeof category === 'object' && category.slug && !category.name) {
            if (typeof category.slug === 'string' && category.slug.trim()) {
              // Convert slug to title case
              const readableCategory = category.slug
                .replace(/-/g, ' ')
                .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
              
              console.log(`Adding category from object with slug: ${category.slug} -> ${readableCategory}`);
              tags.push(readableCategory);
            }
          }
          // If category is a number (ID reference)
          else if (typeof category === 'number') {
            console.log(`Skipping numeric category ID: ${category}`);
          }
        });
      } 
      else if (typeof post.categories === 'object' && post.categories !== null) {
        Object.entries(post.categories).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim()) {
            console.log(`Adding category from object key-value: ${value}`);
            tags.push(value.trim());
          }
          else if (value && typeof value === 'object' && (value as any).name) {
            if (typeof (value as any).name === 'string' && (value as any).name.trim()) {
              console.log(`Adding category from nested object with name: ${(value as any).name}`);
              tags.push((value as any).name.trim());
            }
          }
        });
      }
    }
    
    // 3. Try to extract from term_names if it exists (some WordPress exports use this)
    if (post.term_names) {
      console.log(`Post "${title}" has term_names:`, post.term_names);
      
      // Try different possible term name formats
      if (Array.isArray(post.term_names)) {
        post.term_names.forEach((term: any) => {
          if (typeof term === 'string' && term.trim()) {
            tags.push(term.trim());
          }
        });
      } 
      else if (post.term_names.category && Array.isArray(post.term_names.category)) {
        post.term_names.category.forEach((term: string) => {
          if (typeof term === 'string' && term.trim()) {
            tags.push(term.trim());
          }
        });
      }
      else if (post.term_names.post_tag && Array.isArray(post.term_names.post_tag)) {
        post.term_names.post_tag.forEach((term: string) => {
          if (typeof term === 'string' && term.trim()) {
            tags.push(term.trim());
          }
        });
      }
    }
    
    // 4. Check for embedded terms (common in WordPress REST API responses)
    if (post._embedded && post._embedded['wp:term']) {
      console.log(`Post "${title}" has embedded terms`);
      const terms = post._embedded['wp:term'];
      
      if (Array.isArray(terms)) {
        terms.forEach(termGroup => {
          if (Array.isArray(termGroup)) {
            termGroup.forEach(term => {
              if (term && typeof term === 'object' && term.name) {
                if (typeof term.name === 'string' && term.name.trim()) {
                  console.log(`Adding tag from embedded term: ${term.name}`);
                  tags.push(term.name.trim());
                }
              }
            });
          }
        });
      }
    }
    
    // 5. Check for topics or topic_names (used in some WordPress exports, especially HubSpot)
    if (post.topics && Array.isArray(post.topics)) {
      post.topics.forEach((topic: any) => {
        if (typeof topic === 'string' && topic.trim()) {
          tags.push(topic.trim());
        } 
        else if (topic && typeof topic === 'object' && topic.name) {
          if (typeof topic.name === 'string' && topic.name.trim()) {
            tags.push(topic.name.trim());
          }
        }
      });
    }
    
    if (post.topic_names && Array.isArray(post.topic_names)) {
      post.topic_names.forEach((topic: string) => {
        if (typeof topic === 'string' && topic.trim()) {
          tags.push(topic.trim());
        }
      });
    }
    
    // Remove duplicates from tags
    tags = Array.from(new Set(tags));
    console.log(`Post "${title}" final tags:`, tags);
    
    // Extract featured image
    let coverImage;
    
    if (post.featured_media && post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
      coverImage = post._embedded['wp:featuredmedia'][0].source_url;
      console.log(`Found featured media from _embedded: ${coverImage}`);
    } else if (post.jetpack_featured_media_url) {
      coverImage = post.jetpack_featured_media_url;
      console.log(`Found Jetpack featured media URL: ${coverImage}`);
    } else if (post.featured_image) {
      coverImage = post.featured_image;
      console.log(`Found featured_image property: ${coverImage}`);
    }
    
    // Process featured image if it exists
    if (coverImage) {
      try {
        console.log(`Processing featured image for post: ${title}`);
        coverImage = await processImage(coverImage, slug);
        console.log(`Processed featured image: ${coverImage}`);
      } catch (error) {
        console.error(`Error processing featured image for ${title}:`, error);
        // Keep original URL if processing fails
      }
    }
    
    // Process content to download embedded images
    let processedContent = content;
    try {
      console.log(`Processing content images for post: ${title}`);
      processedContent = await processContentImages(content, slug);
    } catch (error) {
      console.error(`Error processing content images for ${title}:`, error);
      // Keep original content if processing fails
    }
    
    // Extract author
    const author = post.author_name || 
      (post._embedded?.['author']?.[0]?.name) || 
      'edgefi team';
    
    // Calculate reading time if content exists
    const wordCount = processedContent ? processedContent.split(/\s+/).length : 0;
    const readingTime = Math.max(1, Math.ceil(wordCount / 1500));
    
    // Create BlogPost object with tags
    return {
      title,
      slug,
      content: processedContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ''),
      excerpt: excerpt.replace(/<[^>]*>/g, '').substring(0, 300),
      tags: tags.length > 0 ? tags : ['Uncategorized'],
      date,
      author,
      coverImage,
      wordpressId: post.id ? String(post.id) : undefined,
      originalDate: post.date,
      readingTime
    };
  } catch (error) {
    console.error("Error processing WordPress post:", error);
    console.error("Problematic post data:", JSON.stringify(post).substring(0, 200) + "...");
    return null;
  }
};
